import { useState } from "react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { BookingModal } from "@/components/BookingModal";
import { LeadCaptureModal } from "@/components/LeadCapture";
import { StickyCTA } from "@/components/StickyCTA";
import { ScrollProgress } from "@/components/ScrollProgress";
import { InstitutionalForm } from "@/components/InstitutionalForm";
import { Eyebrow, Reveal } from "@/components/bits";
import { useLang } from "@/i18n";

// Editable anonymized opportunity tiles — codes only, never names/addresses.
// Metrics stay literal (units/numbers); class, location, status and band text
// translate via i18n keys.
const TILES = [
  { code: "OPP-01", cls: "tl.cls.hosp", loc: "tl.loc.puriCoast", metrics: ["~2 acres", "Operating"], band: "₹75–100 Cr band", status: "mandate" },
  { code: "OPP-02", cls: "tl.cls.land", loc: "tl.loc.bbsrCorridor", metrics: ["60+ acres", "Highway-linked"], band: "₹2–3 Cr/acre", status: "available" },
  { code: "OPP-03", cls: "tl.cls.health", loc: "tl.loc.central", metrics: ["100+ beds", "Yield asset"], bandKey: "tl.onRequest", status: "offmarket" },
  { code: "OPP-04", cls: "tl.cls.edu", loc: "tl.loc.cuttackRegion", metrics: ["Affiliated", "Land + operations"], band: "POA", status: "mandate" },
  { code: "OPP-05", cls: "tl.cls.land", loc: "tl.loc.puriBelt", metrics: ["CRZ-cleared", "~1 acre"], band: "₹80 Cr band", status: "available" },
  { code: "OPP-06", cls: "tl.cls.agri", loc: "tl.loc.interior", metrics: ["200+ acres", "Clean title"], bandKey: "tl.onRequest", status: "sourcing" },
  { code: "OPP-07", cls: "tl.cls.hosp", loc: "tl.loc.marineDrive", metrics: ["Resort-ready", "~4 acres"], band: "₹150–200 Cr band", status: "offmarket" },
  { code: "OPP-08", cls: "tl.cls.land", loc: "tl.loc.khordha", metrics: ["25–40 acres", "Power & water"], band: "₹1.5–2.5 Cr/acre", status: "available" },
  { code: "OPP-09", cls: "tl.cls.health", loc: "tl.loc.bbsr", metrics: ["60-bed facility", "Running"], band: "₹40–60 Cr band", status: "sourcing" },
  { code: "OPP-10", cls: "tl.cls.edu", loc: "tl.loc.bbsrOutskirts", metrics: ["CBSE-affiliated", "12 acres"], band: "₹90–120 Cr band", status: "mandate" },
  { code: "OPP-11", cls: "tl.cls.land", loc: "tl.loc.nh16", metrics: ["100+ acres", "Logistics-suited"], band: "₹1–2 Cr/acre", status: "available" },
  { code: "OPP-12", cls: "tl.cls.hosp", loc: "tl.loc.chilika", metrics: ["~3 acres", "Approvals in motion"], band: "₹120–150 Cr band", status: "sourcing" },
  { code: "OPP-13", cls: "tl.cls.agri", loc: "tl.loc.western", metrics: ["350+ acres", "Irrigation access"], bandKey: "tl.onRequest", status: "available" },
  { code: "OPP-14", cls: "tl.cls.health", loc: "tl.loc.sambalpur", metrics: ["Diagnostic + 40 beds", "Running"], band: "₹25–40 Cr band", status: "offmarket" },
  { code: "OPP-15", cls: "tl.cls.land", loc: "tl.loc.puriKonark", metrics: ["~5 acres", "Hospitality-zoned"], band: "₹90–110 Cr band", status: "mandate" },
  { code: "OPP-16", cls: "tl.cls.land", loc: "tl.loc.cuttackPeri", metrics: ["15 acres", "Warehouse-ready"], band: "₹2–4 Cr/acre", status: "available" },
  { code: "OPP-17", cls: "tl.cls.edu", loc: "tl.loc.berhampur", metrics: ["Operating school", "2,000+ students"], band: "POA", status: "offmarket" },
  { code: "OPP-18", cls: "tl.cls.hosp", loc: "tl.loc.bbsrCbd", metrics: ["Boutique hotel", "42 keys"], band: "₹55–75 Cr band", status: "mandate" },
  { code: "OPP-19", cls: "tl.cls.land", loc: "tl.loc.airportCorridor", metrics: ["30+ acres", "Mixed-use headroom"], band: "₹3–5 Cr/acre", status: "sourcing" },
  { code: "OPP-20", cls: "tl.cls.health", loc: "tl.loc.coastalCluster", metrics: ["Nursing home", "Running 15 yrs"], bandKey: "tl.onRequest", status: "available" },
];

const STATUS_STYLE = {
  available: "border-[#5E8570]/60 text-[#5E8570]",
  mandate: "border-[#B8894B]/60 text-[#B8894B]",
  sourcing: "border-[#EDE7DA]/30 text-[#EDE7DA]/60",
  offmarket: "border-[#EDE7DA]/20 text-[#EDE7DA]/40",
};

const CAP_TILES = [
  { key: "land", tKey: "cap.land.t", cKey: "cap.land.c", interest: "tl.cls.land" },
  { key: "hosp", tKey: "cap.hosp.t", cKey: "cap.hosp.c", interest: "tl.cls.hosp" },
  { key: "health", tKey: "cap.health.t", cKey: "cap.health.c", interest: "tl.cls.health" },
  { key: "edu", tKey: "cap.edu.t", cKey: "cap.edu.c", interest: "tl.cls.edu" },
];

const STEPS = [
  { num: "01", tKey: "how.s1.t", cKey: "how.s1.c" },
  { num: "02", tKey: "how.s2.t", cKey: "how.s2.c" },
  { num: "03", tKey: "how.s3.t", cKey: "how.s3.c" },
  { num: "04", tKey: "how.s4.t", cKey: "how.s4.c" },
];

// The Odisha Case — every figure references an i18n key; edit strings in i18n.jsx.
const CASE_CLUSTERS = [
  { num: "01", titleKey: "od.c1.t", ctxKey: "od.c1.ctx", stats: ["od.s1", "od.s2", "od.s3", "od.s4", "od.s5"] },
  { num: "02", titleKey: "od.c2.t", ctxKey: "od.c2.ctx", stats: ["od.s6", "od.s7", "od.s8", "od.s9"] },
  { num: "03", titleKey: "od.c3.t", ctxKey: "od.c3.ctx", stats: ["od.s10", "od.s11", "od.s12", "od.s13"] },
];

// Novare's own on-ground observations — deliberately styled apart from public data.
const NOVARE_NOTES = [
  { nKey: "od.nv1.n", cKey: "od.nv1.c" },
  { nKey: "od.nv2.n", cKey: "od.nv2.c" },
];

const Tile = ({ tile, onTap }) => {
  const { t } = useLang();
  return (
    <button
      type="button"
      data-testid={`opp-tile-${tile.code.toLowerCase()}`}
      onClick={() => onTap(tile.cls)}
      className="w-[300px] shrink-0 text-left border border-[#1A2A24] bg-[#131F1A] hover:border-[#B8894B]/60 transition-colors duration-300 p-6"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono-label text-[10px] tracking-[0.25em] text-[#B8894B]">{tile.code}</span>
        <span className={`font-mono-label text-[9px] tracking-[0.12em] uppercase border px-2 py-1 ${STATUS_STYLE[tile.status]}`}>
          {t(`tl.st.${tile.status}`)}
        </span>
      </div>
      <p className="font-display text-lg text-[#EDE7DA] tracking-tight mt-4">{t(tile.cls)}</p>
      <p className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#EDE7DA]/45 mt-1.5">{t(tile.loc)}</p>
      <p className="font-light text-sm text-[#EDE7DA]/60 mt-4">{tile.metrics.join(" · ")}</p>
      <p className="font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#5E8570] mt-4">
        {tile.bandKey ? t(tile.bandKey) : tile.band}
      </p>
    </button>
  );
};

const MarqueeRow = ({ tiles, reverse, onTap }) => (
  <div className="marquee">
    <div className={`marquee-track ${reverse ? "reverse" : ""}`}>
      {[...tiles, ...tiles].map((tile, i) => (
        <Tile key={`${tile.code}-${i}`} tile={tile} onTap={onTap} />
      ))}
    </div>
  </div>
);

const setInstMeta = () => {
  const set = (sel, val) => {
    const el = document.head.querySelector(sel);
    if (el) el.setAttribute("content", val);
  };
  set('meta[property="og:title"]', "You run the fund. We run Odisha. — Novare Estates");
  set('meta[property="og:image"]', `${window.location.origin}/og-institutional.png`);
  set('meta[name="twitter:image"]', `${window.location.origin}/og-institutional.png`);
};

export default function InstitutionalPage() {
  const { t } = useLang();
  setInstMeta();
  const [bookingDoor, setBookingDoor] = useState(null);
  const [leadDoor, setLeadDoor] = useState(null);
  const [presetInterest, setPresetInterest] = useState("");

  const closeLead = () => {
    try {
      window.localStorage.setItem("novare_intro_seen", "1");
    } catch {}
    setLeadDoor(null);
  };

  const goToForm = (interestKey) => {
    if (interestKey) setPresetInterest(interestKey);
    document.getElementById("inst-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div id="top" className="bg-[#0E1613] text-[#EDE7DA] min-h-screen pb-14 md:pb-0">
      <ScrollProgress />
      <Nav onBookCall={() => setBookingDoor("Acquirer")} onEnquire={() => setLeadDoor("general")} />

      <section
        data-testid="inst-hero"
        className="min-h-screen flex flex-col justify-center max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20"
      >
        <p className="hero-reveal font-mono-label text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#B8894B]" style={{ animationDelay: "0.1s" }}>
          {t("in.eyebrow")}
        </p>
        <h1
          className="hero-reveal font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#EDE7DA] leading-[1.08] mt-6 max-w-4xl"
          style={{ animationDelay: "0.25s" }}
        >
          {t("in.h1a")}{" "}
          <em className="italic text-[#B8894B]">{t("in.h1em")}</em>
          {t("in.h1b")}
        </h1>
        <p
          className="hero-reveal font-light text-base md:text-lg text-[#EDE7DA]/65 leading-relaxed mt-6 max-w-2xl"
          style={{ animationDelay: "0.4s" }}
        >
          {t("in.sub")}
        </p>
        <div className="hero-reveal mt-10" style={{ animationDelay: "0.55s" }}>
          <button
            type="button"
            data-testid="inst-hero-cta"
            onClick={() => goToForm("")}
            className="font-mono-label text-xs tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-8 py-4 hover:bg-[#EDE7DA] transition-colors duration-300"
          >
            {t("in.cta")}
          </button>
          <p className="font-display italic text-lg md:text-xl text-[#B8894B] tracking-tight mt-8">
            {t("in.promise")}
          </p>
        </div>
      </section>

      <section data-testid="odisha-case" className="py-24 md:py-32 border-t border-[#1A2A24]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>{t("od.eyebrow")}</Eyebrow>
          {CASE_CLUSTERS.map((cl) => (
            <div key={cl.num} className="mt-16 first:mt-12">
              <div className="flex items-baseline gap-4">
                <span className="font-mono-label text-xs text-[#B8894B]">{cl.num}</span>
                <h3 className="font-display text-2xl md:text-3xl text-[#EDE7DA] tracking-tight">
                  {t(cl.titleKey)}
                </h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                {cl.stats.map((k) => (
                  <div
                    key={k}
                    data-testid={`case-stat-${k.split(".")[1]}`}
                    className="border border-[#1A2A24] bg-[#131F1A] p-7 flex flex-col justify-between"
                  >
                    <div>
                      <p className="font-display text-3xl md:text-4xl text-[#B8894B] tracking-tight">
                        {t(`${k}.n`)}
                      </p>
                      <p className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#EDE7DA]/70 mt-4">
                        {t(`${k}.l`)}
                      </p>
                      <p className="font-light text-sm text-[#EDE7DA]/55 leading-relaxed mt-3">
                        {t(`${k}.c`)}
                      </p>
                    </div>
                    <p className="font-mono-label text-[9px] tracking-[0.15em] uppercase text-[#5E8570] mt-6">
                      {t(`${k}.s`)}
                    </p>
                  </div>
                ))}
              </div>
              <p className="font-light text-sm md:text-base text-[#EDE7DA]/60 leading-relaxed border-l-2 border-[#B8894B] pl-5 mt-8 max-w-3xl">
                {t(cl.ctxKey)}
              </p>
            </div>
          ))}

          <div
            data-testid="case-novare-notes"
            className="mt-16 border border-[#5E8570]/40 border-l-2 border-l-[#5E8570] bg-[#131F1A] p-7 md:p-10"
          >
            <p className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-[#5E8570]">
              {t("od.nv.tag")}
            </p>
            <div className="grid sm:grid-cols-2 gap-8 mt-8">
              {NOVARE_NOTES.map((n) => (
                <div key={n.nKey}>
                  <p className="font-display text-3xl md:text-4xl text-[#5E8570] tracking-tight">
                    {t(n.nKey)}
                  </p>
                  <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-3">
                    {t(n.cKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-20 md:mt-24 border-t border-[#1A2A24] pt-14">
            <p className="font-display text-2xl md:text-4xl text-[#EDE7DA] tracking-tight leading-[1.15] max-w-4xl">
              {t("od.close.a")}{" "}
              <em className="italic text-[#B8894B]">{t("od.close.em")}</em>
            </p>
            <button
              type="button"
              data-testid="case-cta"
              onClick={() => goToForm("")}
              className="mt-9 font-mono-label text-xs tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-8 py-4 hover:bg-[#EDE7DA] transition-colors duration-300"
            >
              {t("in.cta")}
            </button>
            <p className="font-mono-label text-[9px] tracking-[0.18em] uppercase text-[#EDE7DA]/35 mt-12">
              {t("od.sources")}
            </p>
          </div>
        </div>
      </section>

      <section data-testid="inst-capabilities" className="py-24 md:py-32 border-t border-[#1A2A24]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>{t("cap.eyebrow")}</Eyebrow>
          <div className="grid sm:grid-cols-2 gap-4 mt-12">
            {CAP_TILES.map((c, i) => (
              <Reveal key={c.key} delay={i * 90}>
                <button
                  type="button"
                  data-testid={`capability-tile-${c.key}`}
                  onClick={() => goToForm(c.interest)}
                  className="group w-full h-full text-left border border-[#1A2A24] bg-[#131F1A] hover:border-[#B8894B]/70 transition-colors duration-300 p-8 md:p-10"
                >
                  <h3 className="font-display text-xl md:text-2xl text-[#EDE7DA] tracking-tight">
                    {t(c.tKey)}
                  </h3>
                  <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-4">
                    {t(c.cKey)}
                  </p>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="inst-pipeline" className="py-24 md:py-32 border-t border-[#1A2A24] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>{t("pipe.eyebrow")}</Eyebrow>
          <p className="font-mono-label text-[10px] md:text-[11px] tracking-[0.18em] uppercase text-[#EDE7DA]/45 mt-5">
            {t("pipe.label")}
          </p>
        </div>
        <div className="grid gap-4 mt-12">
          <MarqueeRow tiles={TILES.slice(0, 10)} onTap={goToForm} />
          <MarqueeRow tiles={TILES.slice(10)} reverse onTap={goToForm} />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-14 flex flex-col items-start gap-6">
          <p className="font-display italic text-xl md:text-2xl text-[#EDE7DA]/80 tracking-tight max-w-3xl leading-relaxed">
            “{t("pipe.ctaLine")}”
          </p>
          <button
            type="button"
            data-testid="pipeline-cta"
            onClick={() => goToForm("")}
            className="font-mono-label text-[11px] tracking-[0.2em] uppercase border border-[#B8894B] text-[#B8894B] px-7 py-3.5 hover:bg-[#B8894B] hover:text-[#0E1613] transition-colors duration-300"
          >
            {t("pipe.cta")}
          </button>
        </div>
      </section>

      <section data-testid="inst-how" className="py-24 md:py-32 border-t border-[#1A2A24]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>{t("how.eyebrow")}</Eyebrow>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1A2A24] border border-[#1A2A24] mt-12">
            {STEPS.map((s) => (
              <div key={s.num} data-testid={`how-step-${s.num}`} className="bg-[#0E1613] p-8">
                <span className="font-mono-label text-xs text-[#B8894B]">{s.num}</span>
                <h3 className="font-display text-lg md:text-xl text-[#EDE7DA] tracking-tight mt-4">
                  {t(s.tKey)}
                </h3>
                <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-3">
                  {t(s.cKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="inst-why" className="py-24 md:py-32 border-t border-[#1A2A24]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Eyebrow>{t("whyo.eyebrow")}</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#EDE7DA] mt-5 leading-[1.15]">
              {t("whyo.h")}
            </h2>
          </div>
          <div className="md:col-span-7 grid gap-8">
            <p className="font-light text-base md:text-lg text-[#EDE7DA]/65 leading-relaxed border-l-2 border-[#B8894B] pl-6">
              {t("whyo.p1")}
            </p>
            <p className="font-light text-base md:text-lg text-[#EDE7DA]/65 leading-relaxed border-l-2 border-[#5E8570] pl-6">
              {t("whyo.p2")}
            </p>
          </div>
        </div>
      </section>

      <section id="inst-form" data-testid="inst-form-section" className="py-24 md:py-32 border-t border-[#1A2A24]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <Eyebrow>{t("fi.eyebrow")}</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#EDE7DA] mt-5 leading-[1.1]">
              {t("fi.h")}
            </h2>
            <p className="font-light text-base md:text-lg text-[#EDE7DA]/65 leading-relaxed mt-6">
              {t("fi.sub")}
            </p>
          </div>
          <div className="md:col-span-7 border border-[#1A2A24] bg-[#131F1A] p-7 md:p-12">
            <InstitutionalForm presetInterest={presetInterest} />
          </div>
        </div>
      </section>

      <Footer onLead={() => setLeadDoor("general")} />
      <BookingModal door={bookingDoor} onClose={() => setBookingDoor(null)} />
      <LeadCaptureModal door={leadDoor} onClose={closeLead} />
      <StickyCTA onBook={() => setBookingDoor("Acquirer")} onEnquire={() => setLeadDoor("general")} />
    </div>
  );
}
